"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "POLICY OVERVIEW"
}, {
  id: "m-02",
  index: "02",
  label: "COVERAGE LIMITS"
}, {
  id: "m-03",
  index: "03",
  label: "CLAIMS PROCESS"
}, {
  id: "m-04",
  index: "04",
  label: "PREMIUM ADJUSTMENTS"
}, {
  id: "m-05",
  index: "05",
  label: "CANCELLATION TERMS"
}, {
  id: "m-06",
  index: "06",
  label: "PRIVACY NOTICE"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "POLICY OVERVIEW",
  instructions: ["Family Legacy Planning provides comprehensive final expense insurance coverage.", "Policies are designed to cover funeral costs, burial expenses, and outstanding debts.", "Coverage remains active as long as premiums are paid according to the schedule.", "Beneficiaries receive the full death benefit tax-free upon verified claim submission.", "Policyholders must be between the ages of 50 and 85 to qualify for standard plans."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/policy_overview_chart.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/policy_overview_chart.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/policy_overview_chart.webp-wvc-sizes",
    alt: "Chart showing policy coverage structure",
    tag: "[FIG. 01 / COVERAGE]"
  },
  caution: "Coverage is subject to the terms and conditions outlined in your specific policy document."
}, {
  id: "m-02",
  index: "M-02",
  title: "COVERAGE LIMITS",
  instructions: ["Benefit amounts range from $5,000 to $30,000 based on individual selection.", "Coverage amounts are fixed at the time of purchase and do not decrease over time.", "No medical exam is required for approval; eligibility is based on health questions.", "Waiting periods may apply depending on the specific plan and health history.", "Coverage is portable and remains in effect regardless of your state of residence."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/coverage_limits_graph.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/coverage_limits_graph.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/coverage_limits_graph.png-wvc-sizes",
    alt: "Graph showing coverage limit tiers",
    tag: "[FIG. 02 / LIMITS]"
  },
  caution: "Ensure your selected benefit amount aligns with current funeral cost estimates."
}, {
  id: "m-03",
  index: "M-03",
  title: "CLAIMS PROCESS",
  instructions: ["Claims should be filed by the designated beneficiary as soon as possible after passing.", "Required documentation includes a certified copy of the death certificate.", "Our claims department reviews submissions within 5-7 business days.", "Payments are issued directly to the beneficiary via check or electronic transfer.", "We provide dedicated support to assist families through the documentation process."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/claims_process_flow.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/claims_process_flow.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/claims_process_flow.png-wvc-sizes",
    alt: "Flowchart of the claims submission process",
    tag: "[FIG. 03 / 7-DAY WINDOW]"
  },
  caution: "Incomplete documentation will delay the processing of your claim."
}, {
  id: "m-04",
  index: "M-04",
  title: "PREMIUM ADJUSTMENTS",
  instructions: ["Premiums are locked in at the time of enrollment and will never increase.", "Payment options include monthly, quarterly, semi-annual, or annual installments.", "Automatic bank drafts are recommended to ensure timely payments and policy stability.", "Failure to pay premiums within the grace period may result in policy lapse.", "Contact our billing department if you need to update your payment method."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/premium_payment_schedule.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/premium_payment_schedule.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/premium_payment_schedule.png-wvc-sizes",
    alt: "Diagram of premium payment schedule",
    tag: "[FIG. 04 / FIXED RATE]"
  },
  caution: "A 30-day grace period is provided for missed payments before policy termination."
}, {
  id: "m-05",
  index: "M-05",
  title: "CANCELLATION TERMS",
  instructions: ["You may cancel your policy at any time by providing written notice to our office.", "A 30-day free-look period allows for a full refund if you are not satisfied.", "Cash value accumulation depends on the specific type of policy purchased.", "Surrender charges may apply if the policy is canceled within the first few years.", "Once canceled, coverage ends immediately and cannot be reinstated without re-application."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/cancellation_policy_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/cancellation_policy_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/cancellation_policy_diagram.png-wvc-sizes",
    alt: "Diagram of cancellation terms and refund periods",
    tag: "[FIG. 05 / 30-DAY LOOK]"
  },
  caution: "Review your policy document for specific surrender value details."
}, {
  id: "m-06",
  index: "M-06",
  title: "PRIVACY NOTICE",
  instructions: ["We collect personal information solely for the purpose of underwriting and claims.", "Your data is encrypted and stored in secure, HIPAA-compliant environments.", "We do not sell or share your personal information with third-party marketers.", "You have the right to request a copy of the information we hold about you.", "Privacy policies are updated annually to ensure compliance with federal regulations."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/privacy_security_badge.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/privacy_security_badge.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727743/img/privacy_security_badge.webp-wvc-sizes",
    alt: "Security badge representing data privacy",
    tag: "[FIG. 06 / SECURE DATA]"
  },
  caution: "Always verify that you are communicating with an authorized representative."
}];
export default function Legal() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="legaltermsanddisclaimers" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-secondary" aria-hidden="true" />
            <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Legal & Disclaimers
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-black uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              Policy{" "}
              <em className="not-italic md:italic font-black" style={{
              color: "var(--secondary)",
              fontStyle: "italic"
            }}>
                Terms
              </em>{" "}
              & Disclaimers.
            </h1>
            <div className="shrink-0">
              <span className="inline-block font-default text-xs font-semibold uppercase tracking-[0.12em] text-foreground border border-foreground px-3 py-1.5">
                [REV. 2025.01 / LEGAL]
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.85
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-default text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-secondary bg-secondary text-secondary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-default text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-secondary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-secondary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-default text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-secondary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-secondary text-secondary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-default text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-secondary" aria-hidden="true" />
                            Details
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-default text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-default text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--secondary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}